import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { FilterValues } from "../../../data-model/filterValues";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { TableNames } from "../../../enums/tableNames";
import { makePostCallForNextPagesDataFetch } from "../apis/makePostCallForNextPagesDataFetch";
import { buildNycbsExternalFilterPayload } from "../utils/buildNycbsExternalFilterPayload";
import { NycbsExternalFilterPayload } from "../utils/nycbsExternalFilterPayload";
import {
  useFilterPayload,
} from "./useFilterPayload";
import { NycbsExternalTableState } from "../types/nycbsExternalTableState";

export interface UseNextPageDataFetchReturn {
  endIndex: number;
  setEndIndex: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * Fetches additional pages when the user scrolls near the end of loaded rows.
 *
 * @param validFilters - Active sidebar filters for this table
 * @param searchTerm - Current search text
 * @param initialDataFetchingExecuted - Whether the initial fetch has completed
 * @param nycbsExternalState - Current table pagination and loading state
 * @param setNycbsExternalState - Updates table loading and pagination state
 * @param setTableData - Appends newly fetched rows
 * @param dateFromStart - Default range start date (YYYYMMDD)
 * @param dateFromEnd - Default range end date (YYYYMMDD)
 * @param pageSize - Number of rows per page
 * @param orgId - BigQuery organization id (Non-STAT or STAT)
 * @return UseNextPageDataFetchReturn: Scroll end index state
 */
export const useNextPageDataFetch = (
  validFilters: FilterValues[],
  searchTerm: string,
  initialDataFetchingExecuted: boolean,
  nycbsExternalState: NycbsExternalTableState,
  setNycbsExternalState: React.Dispatch<
    React.SetStateAction<NycbsExternalTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<PharmaStpFileModel[]>>,
  dateFromStart: string,
  dateFromEnd: string,
  pageSize: number = 120,
  orgId: string,
): UseNextPageDataFetchReturn => {
  const { setTableDataForContext } = useTablesContext();
  const [endIndex, setEndIndex] = useState<number>(0);

  const lastRequestedPageRef = useRef<number>(0);

  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    dateFromStart,
    dateFromEnd,
    pageSize,
    orgId,
  );

  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  const setNycbsExternalStateRef = useRef(setNycbsExternalState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setNycbsExternalStateRef.current = setNycbsExternalState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [
    initialDataFetchingExecuted,
    setNycbsExternalState,
    setTableData,
    setTableDataForContext,
  ]);

  useEffect(() => {
    lastRequestedPageRef.current = 0;
  }, [validFilters, searchTerm, dateFromStart, dateFromEnd]);

  const shouldFetchMoreData = useMemo(() => {
    const currentPageRowCount = nycbsExternalState.data?.row_count ?? 0;
    const currentPage = nycbsExternalState.data?.page ?? 1;
    const totalPages = nycbsExternalState.data?.total_pages ?? 1;
    const nextPageToFetch = currentPage + 1;

    const previousPagesRows = (currentPage - 1) * pageSize;
    const cumulativeRowsLoaded = previousPagesRows + currentPageRowCount;

    return (
      endIndex >= cumulativeRowsLoaded - 1 &&
      currentPage < totalPages &&
      !nycbsExternalState.loading &&
      !nycbsExternalState.showInlineLoader &&
      nextPageToFetch > lastRequestedPageRef.current
    );
  }, [
    endIndex,
    nycbsExternalState.data?.row_count,
    nycbsExternalState.data?.page,
    nycbsExternalState.data?.total_pages,
    nycbsExternalState.loading,
    nycbsExternalState.showInlineLoader,
    pageSize,
  ]);

  const payloadFinal = useMemo<NycbsExternalFilterPayload>(() => {
    if (filterPayload !== null) {
      return filterPayload;
    }

    return buildNycbsExternalFilterPayload(
      validFilters,
      searchTerm,
      dateFromStart,
      dateFromEnd,
      pageSize,
      orgId,
    );
  }, [
    filterPayload,
    validFilters,
    searchTerm,
    dateFromStart,
    dateFromEnd,
    pageSize,
    orgId,
  ]);

  const payloadFinalWithPage = useMemo<NycbsExternalFilterPayload>(() => {
    return {
      ...payloadFinal,
      page: (nycbsExternalState.data?.page ?? 0) + 1,
      request_id: uuidv4(),
    };
  }, [payloadFinal, nycbsExternalState.data?.page]);

  const currentPage = nycbsExternalState.data?.page ?? 0;

  const fetchNextPage = useCallback(() => {
    const nextPage = currentPage + 1;
    lastRequestedPageRef.current = nextPage;

    makePostCallForNextPagesDataFetch(
      payloadFinalWithPage,
      setNycbsExternalStateRef.current,
      setTableDataRef.current,
    );
    setTableDataForContextRef.current(TableNames.NYCBS_EXTERNAL_ORDERS, {
      lastBatchFetched: nextPage,
    });
  }, [payloadFinalWithPage, currentPage]);

  useEffect(() => {
    if (shouldFetchMoreData && initialDataFetchingExecutedRef.current) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  return {
    endIndex,
    setEndIndex,
  };
};
